// The daily cap on automatic investigations, and the alarms it held for a person (#849),
// read off `routes/automation.py`. The count resets at the workspace's day boundary, which is
// the UTC day's: a workspace has no time zone, so `time_zone` is always "UTC" today.

import type { Api } from "./http";

export const AUTOMATION_PATH = "/api/v1/automation";
export const HELD_PATH = "/api/v1/held-alerts";

/** An alarm the cap held, waiting for a person to start its investigation or dismiss it. */
export interface HeldAlarm {
  id: string;
  held_at: string;
  alarm: string;
  severity: string;
  /** The alert source's name, or null when it is gone. */
  source: string | null;
  source_id: string;
  account_id: string;
  region: string;
  /** How many times it fired while held: a refire joins the alarm it repeats. */
  occurrences: number;
  last_seen_at: string;
  /** Whether its alert has resolved since it was held (#871): it still waits for a person. */
  resolved: boolean;
}

export interface AutomationRead {
  /** The workspace's own limit, or null for the default. */
  daily_cap: number | null;
  default_daily_cap: number;
  max_daily_cap: number;
  /** Today's automatic starts, or null for a reader the count is not told to (#1104): an
   *  organisation visiting a workspace that has an AWS Organization connection. */
  started_today: number | null;
  resets_at: string;
  time_zone: string;
  waiting: HeldAlarm[];
}

/** The limit in force: the workspace's own, or the default. */
export const capOf = (read: Pick<AutomationRead, "daily_cap" | "default_daily_cap">): number =>
  read.daily_cap ?? read.default_daily_cap;

export const readAutomation = (api: Api): Promise<AutomationRead> => api.get<AutomationRead>(AUTOMATION_PATH);

export const saveDailyCap = (api: Api, dailyCap: number | null): Promise<AutomationRead> =>
  api.put<AutomationRead>(AUTOMATION_PATH, { daily_cap: dailyCap });

/** Start a held alarm's investigation now. `joined` when an open one already had its alarm. */
export const investigateHeld = (api: Api, id: string): Promise<{ run_id: string | null; joined: boolean }> =>
  api.post<{ run_id: string | null; joined: boolean }>(`${HELD_PATH}/${encodeURIComponent(id)}/investigate`);

export const dismissHeld = (api: Api, id: string): Promise<void> =>
  api.post<void>(`${HELD_PATH}/${encodeURIComponent(id)}/dismiss`);

/** A severity in words: `critical` reads "Critical"; the value is the alarm's own. */
export const severityLabel = (severity: string): string =>
  severity === "" ? "No severity" : severity[0].toUpperCase() + severity.slice(1);

/** `2026-09-28 00:00 UTC`: one reading of the time for every viewer, as the day is UTC's. */
export function utcTime(at: string): string {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}
